/**
 * Matching Entity Service
 * Service for searching and matching entities from admin database with data validation rows
 * Searches by: First Name, Last Name, Business Name, GIIN, TaxpayerID, Foreign Tax Payer ID
 */

import { BASE_URL } from "../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../API_Endpoints/APIEndpoints";
import { isAdminApiEnabled } from "./AdminApiService";

export interface MatchingSearchCriteria {
  firstName?: string;
  lastName?: string;
  businessName?: string;
  ein?: string;
  tin?: string;
  itin?: string;
  ptin?: string;
  qiein?: string;
  ssnOrItin?: string;
  foreignTaxPayerId?: string;
  wpEin?: string;
  wtEin?: string;
  giin?: string;
}

export interface MatchedEntity {
  entity?: {
    uniqueIdentifier?: string;
    businessName?: string;
    type?: string;
    businessUnit?: string;
    [key: string]: any;
  };
  form?: {
    formInstructorIdentifier?: string;
    uniqueIdentifier?: string;
    formId?: string;
    businessName?: string;
    [key: string]: any;
  };
  uniqueIdentifier?: string;
  formInstructorIdentifier?: string;
  [key: string]: any;
}

export interface DataValidationRow {
  rowId?: string | number;
  uniqueIdentifier?: string;
  "Unique Identifier"?: string;
  businessName?: string;
  "Business Name"?: string;
  entityType?: string;
  "Entity Type"?: string;

  // Tax identification numbers - Business Entities tab
  ein?: string;
  EIN?: string;
  tin?: string;
  TIN?: string;
  itin?: string;
  ITIN?: string;
  ptin?: string;
  PTIN?: string;
  qiein?: string;
  QIEIN?: string;
  "QI-EIN"?: string;
  ssnOrItin?: string;
  "SSN or ITIN"?: string;
  foreignTaxPayerId?: string;
  "Foreign Taxpayer Country Code"?: string;
  "Foreign Taxpayer ID"?: string;
  wpEin?: string;
  "WP-EIN"?: string;
  wtEin?: string;
  "WT-EIN"?: string;
  giin?: string;
  GIIN?: string;

  // Accounts tab fields
  firstName?: string;
  "First Name"?: string;
  lastName?: string;
  "Last Name"?: string;

  formType?: string;
  "Form Type"?: string;
  formRevision?: string;
  "Form Revision"?: string;
  [key: string]: any;
}

export interface MatchingResult extends DataValidationRow {
  hasMatches: boolean;
  matchCount: number;
  matchedEntities?: MatchedEntity[];
  isMatchRow?: boolean; // Flag to identify if this is a matched entity row
  parentRowId?: string | number; // Reference to parent row if this is a match
}

/**
 * Search for matching entities using the SearchForm API endpoint
 * @param criteria - Search criteria extracted from data validation row
 * @returns Array of matched entities
 */
export const searchMatchingEntities = async (
  criteria: MatchingSearchCriteria
): Promise<MatchedEntity[]> => {
  try {
    // Check if Admin API is enabled first (using centralized service)
    const isAdminEnabled = await isAdminApiEnabled();
    if (!isAdminEnabled) {
      console.log("⚠️ Send To Admin API is disabled - skipping entity matching");
      return [];
    }

    const userData = getUserIdFromLocalStorage();

    if (!userData || !userData.token) {
      console.error("No user token available");
      return [];
    }

    // Extract search parameters
    const {
      firstName,
      lastName,
      businessName,
      ein,
      tin,
      itin,
      ptin,
      qiein,
      ssnOrItin,
      foreignTaxPayerId,
      wpEin,
      wtEin,
      giin,
    } = criteria;

    // Determine search term with priority: business name > first/last name combination
    let searchTerm = "";
    if (businessName?.trim()) {
      searchTerm = businessName.trim();
    } else if (firstName?.trim() || lastName?.trim()) {
      searchTerm = `${firstName || ""} ${lastName || ""}`.trim();
    }

    // Collect all possible TIN values and use the first non-empty one
    const tinValue =
      ein?.trim() ||
      tin?.trim() ||
      itin?.trim() ||
      ptin?.trim() ||
      qiein?.trim() ||
      ssnOrItin?.trim() ||
      foreignTaxPayerId?.trim() ||
      wpEin?.trim() ||
      wtEin?.trim() ||
      "";

    const giinValue = giin?.trim() || "";

    // Don't call API if all search criteria are empty
    if (!searchTerm && !tinValue && !giinValue) {
      console.log("SearchForm API call skipped - all search criteria are empty");
      return [];
    }

    // Create request body matching the API format
    const requestBody = {
      UID: null,
      Name: searchTerm || null,
      Tin: tinValue || null,
      Giin: giinValue || null,
      SignatureDate: null,
      FormType: null,
      Revision: null,
      ShowOrphan: false,
    };

    const response = await fetch(`${BASE_URL}/Utilities/SearchForm`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userData.token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      console.error("SearchForm API error:", response.status);
      return [];
    }

    const apiResult = await response.json();
    console.log("✅ SearchForm API response:", apiResult);

    // Extract entities from the result
    // Priority: Use entity array if not empty, otherwise use forms array
    let matchedEntities: MatchedEntity[] = [];
    const uniqueEntities = new Map<string, MatchedEntity>();

    if (Array.isArray(apiResult)) {
      apiResult.forEach((item) => {
        const hasEntityArray = Array.isArray(item.entity) && item.entity.length > 0;

        if (hasEntityArray) {
          // Entity array is not empty - use it and skip forms array
          console.log("✅ Using entity array (not empty)");
          item.entity.forEach((entity: any) => {
            const uid = entity.uniqueIdentifier || entity.entity?.uniqueIdentifier;
            if (uid && !uniqueEntities.has(uid)) {
              uniqueEntities.set(uid, {
                entity: entity,
                uniqueIdentifier: uid,
              });
            }
          });
        } else if (Array.isArray(item.forms)) {
          // Entity array is empty - use forms array
          console.log("✅ Entity array is empty, using forms array");
          item.forms.forEach((formItem: any) => {
            const entity = formItem.entity;
            if (entity) {
              const uid = entity.uniqueIdentifier;
              if (uid && !uniqueEntities.has(uid)) {
                // Store the entity with form reference
                uniqueEntities.set(uid, {
                  entity: entity,
                  form: formItem.form,
                  uniqueIdentifier: uid,
                });
              }
            }
          });
        }
      });
    }

    matchedEntities = Array.from(uniqueEntities.values());
    console.log(`✅ Found ${matchedEntities.length} unique matching entities`);
    return matchedEntities;
  } catch (error) {
    console.error("❌ Error searching entities:", error);
    return [];
  }
};

/**
 * Process data validation rows and add matching entity rows beneath each matched row
 * @param rows - Array of data validation rows
 * @returns Enhanced array with matching entity rows inserted
 */
export const processRowsWithMatches = async (
  rows: DataValidationRow[]
): Promise<MatchingResult[]> => {
  const processedRows: MatchingResult[] = [];

  for (const row of rows) {
    // Add the original row first
    const enhancedRow: MatchingResult = {
      ...row,
      hasMatches: false,
      matchCount: 0,
    };

    // Extract search criteria from the row - Business Entities tab fields
    const criteria: MatchingSearchCriteria = {
      firstName: row.FirstName || row.firstName || row["First Name"] || "",
      lastName: row.LastName || row.lastName || row["Last Name"] || "",
      businessName: row["Business Name"] || row.businessName || "",
      ein: row.EIN || row.ein || "",
      tin: row.TIN || row.tin || "",
      itin: row.ITIN || row.itin || "",
      ptin: row.PTIN || row.ptin || "",
      qiein: row.QIEIN || row.qiein || row["QI-EIN"] || "",
      ssnOrItin: row.SSNOrITIN || row.ssnOrItin || row["SSN or ITIN"] || "",
      foreignTaxPayerId:
        row.ForeignTaxpayerId || row.foreignTaxPayerId || row["Foreign Taxpayer ID"] || "",
      wpEin: row["WP-EIN"] || row.wpEin || "",
      wtEin: row["WT-EIN"] || row.wtEin || "",
      giin: row.GIIN || row.giin || "",
    };

    // Search for matching entities
    const matchedEntities = await searchMatchingEntities(criteria);

    if (matchedEntities.length > 0) {
      enhancedRow.hasMatches = true;
      enhancedRow.matchCount = matchedEntities.length;
      enhancedRow.matchedEntities = matchedEntities;
    }

    processedRows.push(enhancedRow);

    // Add matched entity rows beneath the parent row
    if (matchedEntities.length > 0) {
      // Build a set of existing unique identifiers to check for duplicates
      const existingUniqueIdentifiers = new Set<string>();
      rows.forEach((r) => {
        const uid = r["Unique Identifier"] || r.uniqueIdentifier || r.UniqueIdentifier;
        if (uid && uid !== "N/A" && uid.trim() !== "") {
          existingUniqueIdentifiers.add(uid.trim().toLowerCase());
        }
      });

      matchedEntities.forEach((entity, index) => {
        const uniqueId = entity.entity?.uniqueIdentifier || entity.uniqueIdentifier || "N/A";

        // Skip if this unique identifier already exists in the original data
        if (uniqueId !== "N/A" && existingUniqueIdentifiers.has(uniqueId.trim().toLowerCase())) {
          console.log(`⚠️ Skipping duplicate unique identifier: ${uniqueId}`);
          return;
        }

        // Get display name (businessName or individualName)
        const displayName = getEntityDisplayName(entity.entity);

        // Split individual name if businessName is null
        const nameParts = entity.entity?.businessName
          ? { firstName: "", lastName: "" }
          : splitIndividualName(entity.entity?.individualName);

        const matchRow: MatchingResult = {
          rowId: `${row.rowId}_match_${index}`,
          isMatchRow: true,
          parentRowId: row.rowId,
          hasMatches: false,
          matchCount: 0,

          // Extract entity/form data for display
          businessName: displayName,
          firstName: nameParts.firstName,
          lastName: nameParts.lastName,
          uniqueIdentifier: uniqueId,
          formInstructorIdentifier:
            entity.form?.formInstructorIdentifier ||
            entity.form?.uniqueIdentifier ||
            entity.formInstructorIdentifier ||
            "N/A",
          type: entity.entity?.type || "N/A",
          businessUnit: entity.entity?.businessUnit || "N/A",

          // Store full entity data for reference
          entityData: entity,
        };

        processedRows.push(matchRow);
      });
    }
  }

  return processedRows;
};

/**
 * Search for a single row and return matches
 * @param row - Single data validation row
 * @returns Matching result with entities
 */
export const searchSingleRow = async (row: DataValidationRow): Promise<MatchingResult> => {
  const criteria: MatchingSearchCriteria = {
    firstName: row.FirstName || row.firstName || row["First Name"] || "",
    lastName: row.LastName || row.lastName || row["Last Name"] || "",
    businessName: row["Business Name"] || row.businessName || "",
    ein: row.EIN || row.ein || "",
    tin: row.TIN || row.tin || "",
    itin: row.ITIN || row.itin || "",
    ptin: row.PTIN || row.ptin || "",
    qiein: row.QIEIN || row.qiein || row["QI-EIN"] || "",
    ssnOrItin: row.SSNOrITIN || row.ssnOrItin || row["SSN or ITIN"] || "",
    foreignTaxPayerId:
      row.ForeignTaxpayerId || row.foreignTaxPayerId || row["Foreign Taxpayer ID"] || "",
    wpEin: row["WP-EIN"] || row.wpEin || "",
    wtEin: row["WT-EIN"] || row.wtEin || "",
    giin: row.GIIN || row.giin || "",
  };

  const matchedEntities = await searchMatchingEntities(criteria);

  return {
    ...row,
    hasMatches: matchedEntities.length > 0,
    matchCount: matchedEntities.length,
    matchedEntities: matchedEntities.length > 0 ? matchedEntities : undefined,
  };
};

/**
 * Get row style based on whether it's a match row
 * @param isMatchRow - Whether this is a matched entity row
 * @returns Style object with background color
 */
export const getMatchRowStyle = (isMatchRow?: boolean) => {
  if (isMatchRow) {
    return {
      backgroundColor: "#e8f5e9", // Light green background for match rows
      borderLeft: "4px solid #4caf50", // Green left border for emphasis
    };
  }
  return {};
};

/**
 * Split individual name into first and last name
 * @param individualName - Full name string (e.g., "Jodie United-Kindom-Five")
 * @returns Object with firstName and lastName
 */
export const splitIndividualName = (
  individualName: string | null | undefined
): { firstName: string; lastName: string } => {
  if (!individualName || !individualName.trim()) {
    return { firstName: "", lastName: "" };
  }

  const nameParts = individualName.trim().split(" ");

  if (nameParts.length === 1) {
    return { firstName: nameParts[0], lastName: "" };
  }

  // First part is firstName, rest is lastName
  const firstName = nameParts[0];
  const lastName = nameParts.slice(1).join(" ");

  return { firstName, lastName };
};

/**
 * Get display name for entity (businessName or individualName split)
 * @param entity - Entity object
 * @returns Display name string
 */
export const getEntityDisplayName = (entity: any): string => {
  if (entity?.businessName) {
    return entity.businessName;
  }

  if (entity?.individualName) {
    return entity.individualName;
  }

  return "N/A";
};

/**
 * Check if any search criteria exists in a row
 * @param row - Data validation row
 * @returns true if row has searchable data
 */
export const hasSearchableCriteria = (row: DataValidationRow): boolean => {
  const firstName = row.FirstName || row.firstName || row["First Name"] || "";
  const lastName = row.LastName || row.lastName || row["Last Name"] || "";
  const businessName = row["Business Name"] || row.businessName || "";
  const ein = row.EIN || row.ein || "";
  const tin = row.TIN || row.tin || "";
  const itin = row.ITIN || row.itin || "";
  const giin = row.GIIN || row.giin || "";
  const foreignTaxPayerId =
    row.ForeignTaxpayerId || row.foreignTaxPayerId || row["Foreign Taxpayer ID"] || "";

  return !!(
    firstName.trim() ||
    lastName.trim() ||
    businessName.trim() ||
    ein.trim() ||
    tin.trim() ||
    itin.trim() ||
    giin.trim() ||
    foreignTaxPayerId.trim()
  );
};

export default {
  searchMatchingEntities,
  processRowsWithMatches,
  searchSingleRow,
  getMatchRowStyle,
  hasSearchableCriteria,
};
